/**
 * Setup Wizard Types
 * Type definitions for the first-time user setup wizard
 */

// Step identifiers
export type SetupWizardStepId = 'welcome' | 'profile' | 'entity' | 'complete';

// Individual step configuration
export interface SetupWizardStep {
  id: SetupWizardStepId;
  title: string;
  description: string;
  skippable: boolean;
}

// Step definitions
export const SETUP_WIZARD_STEPS: SetupWizardStep[] = [
  {
    id: 'welcome',
    title: 'Welcome',
    description: 'Get started with LegalSyncUp',
    skippable: false,
  },
  {
    id: 'profile',
    title: 'Your Profile',
    description: 'Tell us about yourself',
    skippable: true,
  },
  {
    id: 'entity',
    title: 'First Entity',
    description: 'Register your first Swiss entity',
    skippable: true,
  },
  {
    id: 'complete',
    title: 'All Set!',
    description: 'You\'re ready to get started',
    skippable: false,
  },
];

// Wizard progress stored in user profile
export interface SetupWizardProgress {
  currentStep: number;
  completedSteps: SetupWizardStepId[];
  skippedAt?: string | null;
  entityCreated: boolean;
  firstEntityId?: string | null;
}

// Default progress for new users
export const DEFAULT_WIZARD_PROGRESS: SetupWizardProgress = {
  currentStep: 0,
  completedSteps: [],
  skippedAt: null,
  entityCreated: false,
  firstEntityId: null,
};

// Profile form data collected in step 2
export interface SetupProfileData {
  firstName: string;
  lastName: string;
  companyName?: string;
  jobTitle?: string;
  timezone: string;
  language: 'de' | 'fr' | 'it' | 'en';
}

// Entity data collected in step 3 (simplified version of full entity)
export interface SetupEntityData {
  name: string;
  uid: string;
  legal_form: string;
  registration_canton: string;
  registration_date: string;
  status: 'active' | 'inactive' | 'archived' | 'dissolved';
  purpose?: string;
  capital?: string;
}

// Wizard context value
export interface SetupWizardContextValue {
  // Current state
  currentStep: number;
  totalSteps: number;
  currentStepConfig: SetupWizardStep;
  progress: SetupWizardProgress;
  isOpen: boolean;

  // Navigation
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: number) => void;
  skipWizard: () => void;
  completeWizard: () => void;

  // Data collection
  setProfileData: (data: Partial<SetupProfileData>) => void;
  setEntityData: (data: SetupEntityData | null) => void;
  profileData: Partial<SetupProfileData>;
  entityData: SetupEntityData | null;

  // Loading states
  isLoading: boolean;
  isSaving: boolean;
}
